import { useNavigate } from '@tanstack/react-router';

import type { Contact } from '@/api/types/contacts';
import { useModal } from '@/components/modal/useModal';
import {
  Avatar,
  Button,
  Icon,
  IconButton,
  MenuItem,
  Popover,
  colorFromKey,
  initialsOf,
  useToast,
} from '@/components/ui';

import { ContactStatusPills } from '../ContactStatusPills';
import type { ContactFormValues } from '../contactSchema';

interface ContactDetailHeroProps {
  values: ContactFormValues;
  contactId?: string;
  rawContact?: Contact;
  sectionsMenu?: React.ReactNode;
  onAddNote?: () => void;
  onDelete?: () => void;
  canDelete?: boolean;
}

export function ContactDetailHero({
  values,
  contactId,
  rawContact,
  sectionsMenu,
  onAddNote,
  onDelete,
  canDelete,
}: ContactDetailHeroProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { openModal } = useModal();
  const { profile, contact, status } = values;
  const fullName = [profile.titleBefore, profile.firstName, profile.lastName, profile.titleAfter]
    .filter(Boolean)
    .join(' ');
  const initials = initialsOf(profile.firstName, profile.lastName);
  const colorKey = contactId ?? `${profile.firstName}-${profile.lastName}`;

  return (
    <div className="detail-hero">
      <Avatar
        initials={initials || '?'}
        color={colorFromKey(colorKey)}
        size={56}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <h1>{fullName || 'New contact'}</h1>
        <div className="hero-sub">
          {contact.primaryEmail && (
            <span>
              <Icon name="mail" size={12} />
              {contact.primaryEmail}
            </span>
          )}
          {contact.phones?.[0]?.number && (
            <span>
              <Icon name="phone" size={12} />
              {contact.phones[0].number}
            </span>
          )}
          {profile.residence && (
            <span>
              <Icon name="mapPin" size={12} />
              {profile.residence}
            </span>
          )}
          <ContactStatusPills status={status} />
        </div>
      </div>
      <div className="hero-actions">
        {sectionsMenu}
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Icon name="send" size={13} />}
          onClick={() => {
            if (contact.primaryEmail) window.location.href = `mailto:${contact.primaryEmail}`;
          }}
          disabled={!contact.primaryEmail}
        >
          Email
        </Button>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Icon name="note" size={13} />}
          onClick={onAddNote}
          disabled={!onAddNote}
        >
          Note
        </Button>
        <Popover
          placement="bottom-end"
          trigger={<IconButton icon="moreH" label="More actions" variant="bordered" />}
        >
          <div style={{ width: 210, padding: 4 }}>
            <MenuItem
              icon={<Icon name="copy" size={13} />}
              disabled={!contactId}
              onClick={() => {
                if (!contactId) return;
                void navigator.clipboard?.writeText(contactId);
                toast.show({ title: 'Contact ID copied', severity: 'success' });
              }}
            >
              Copy contact ID
            </MenuItem>
            <MenuItem
              icon={<Icon name="database" size={13} />}
              disabled={!rawContact}
              onClick={() =>
                rawContact && openModal(<RawJsonView contact={rawContact} />, { size: 'lg' })
              }
            >
              View raw JSON
            </MenuItem>
            <MenuItem
              icon={<Icon name="history" size={13} />}
              disabled={!contactId}
              onClick={() =>
                contactId &&
                navigate({
                  to: '/audit-logs',
                  search: { page: 0, pageSize: 25, q: contactId },
                })
              }
            >
              View in audit log
            </MenuItem>
            {canDelete && onDelete && contactId && (
              <MenuItem
                icon={<Icon name="trash" size={13} />}
                danger
                onClick={onDelete}
              >
                Delete contact
              </MenuItem>
            )}
          </div>
        </Popover>
      </div>
    </div>
  );
}

// Read-only viewer for the raw contact document exactly as it comes back from the
// API/DB — handy for debugging field-level data. Rendered inside the shared modal.
function RawJsonView({ contact }: { contact: Contact }) {
  const toast = useToast();
  const { closeModal } = useModal();
  const json = JSON.stringify(contact, null, 2);

  return (
    <>
      <div className="ds-modal-head">
        <h2>Raw JSON</h2>
      </div>
      <div className="ds-modal-body">
        <pre
          style={{
            margin: 0,
            maxHeight: '60vh',
            overflow: 'auto',
            fontFamily: 'var(--font-mono)',
            fontSize: 11.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: 'var(--text-2)',
          }}
        >
          {json}
        </pre>
      </div>
      <div className="ds-modal-foot">
        <span style={{ flex: 1 }} />
        <Button
          variant="secondary"
          leftIcon={<Icon name="copy" size={13} />}
          onClick={() => {
            void navigator.clipboard?.writeText(json);
            toast.show({ title: 'JSON copied', severity: 'success' });
          }}
        >
          Copy
        </Button>
        <Button variant="ghost" onClick={closeModal}>
          Close
        </Button>
      </div>
    </>
  );
}
